/**
 * Budżet „około” i zakres dla wykonawców (makieta 12, D15; kontrakt zlecenia w repo VV
 * `zadania/2026-10_plan-rynek-dla-domu-produkcja.md`, sekcja 4).
 *
 * Klient podaje kwotę na całość. Wykonawcy widzą zakres od 90% do 105% tej kwoty (2/3 poniżej, 1/3 powyżej: klient
 * nie może się przestraszyć góry, właściciel 2026-10-24), zawsze z budżetu, bez przycinania do zwykłych cen (decyzja
 * 2026-10-25, F10). Zaokrąglenie do 500 zł. Zakres wiążący liczy backend przy wystawieniu; tu jest podgląd
 * „Co zobaczą wykonawcy”.
 *
 * `zrodlo`: `budzet`, `ponizej` (jak `zakres_zrodlo` w kontrakcie) albo `powyzej` (tylko informacja na ekranie).
 */

/** Do pełnych 500 zł, połówki w górę: tak samo jak backend (`rynek/przetarg._do_500`), żeby podgląd zgadzał się z zakresem. */
export const do500 = (n) => Math.floor(n / 500 + 0.5) * 500

/**
 * Zakres dla wykonawców: zawsze 90% do 105% kwoty, bez przycinania do zwykłych cen (decyzja właściciela 2026-10-25,
 * uwagi przeglądu F10). Zwykłe ceny tylko oznaczają odchylenie: `ponizej` (jak backend) albo `powyzej` (sama informacja
 * na ekranie; backend zapisuje wtedy `budzet`).
 * @param {number} kwota kwota, w której wykonawcy wyceniają (przy zestawie zamawianym samodzielnie: budżet minus zestaw)
 * @param {{od:number, do:number}|null} zwykle zwykłe ceny takiej instalacji albo null, gdy ich nie znamy
 */
export const zakresDlaWykonawcow = (kwota, zwykle = null) => {
  const od = do500(kwota * 0.9)
  const gora = do500(kwota * 1.05)
  if (zwykle?.od && gora < zwykle.od) return { od, do: gora, zrodlo: 'ponizej' }
  if (zwykle?.do && od > zwykle.do) return { od, do: gora, zrodlo: 'powyzej' }
  return { od, do: gora, zrodlo: 'budzet' }
}

/** Kwota z pola „Około”: same cyfry („46 000 zł” → 46000), 0 przy pustym. */
export const kwotaZPola = (tekst) => Number(String(tekst ?? '').replace(/\D/g, '')) || 0

/** „46 000” do pola po wyjściu z niego. */
export const formatPola = (n) => (n ? n.toLocaleString('pl-PL') : '')

const zl = (n) => `${Math.round(n).toLocaleString('pl-PL')} zł`
const zakresZl = (a, b) => `${Math.round(a).toLocaleString('pl-PL')} do ${zl(b)}`

/**
 * Wszystko, co pokazuje pasek „Co zobaczą wykonawcy”: wartość w nagłówku, tekst, pasy skali (w procentach) i ton.
 * `zwykle`: `{od, do, zestaw_cena?, tylkoMontaz?}`. `zestaw_cena`, gdy zestaw zamawia klient (wykonawcy wyceniają
 * montaż). `tylkoMontaz` bez ceny zestawu: widełki samego montażu nie porównują się z budżetem na całość, więc są tylko
 * informacją. `uwaga`: budżet odbiega od zwykłych cen (ekran pokazuje wykrzyknik, F10).
 */
export const podgladBudzetu = (kwota, zwykle = null) => {
  const zestaw = zwykle?.zestaw_cena || 0
  const ma = Boolean(zwykle && zwykle.od && zwykle.do)
  const samMontaz = ma && zwykle.tylkoMontaz && !zestaw
  const porownuj = ma && !samMontaz
  const co = zestaw ? 'montaż' : 'taka instalacja'
  const wstep = zestaw
    ? [`Zestaw z Twojego projektu kosztuje w sklepach ok. `, { b: zl(zestaw) }, ', a wykonawcy wyceniają tylko montaż i formalności. ']
    : []
  const montazInfo = samMontaz ? [' Sam montaż, bez sprzętu, kosztuje zwykle ', { b: zakresZl(zwykle.od, zwykle.do) }, '.'] : []
  const wynik = { wartosc: '', tekst: [], uwaga: false, zakres: null, znak: 0, pasy: null }

  if (!kwota) {
    wynik.wartosc = ma ? zakresZl(zwykle.od, zwykle.do) : 'bez kwoty'
    wynik.tekst = ma
      ? [...wstep, `${zestaw || samMontaz ? 'Montaż' : 'Taka instalacja'} kosztuje zwykle `, { b: zakresZl(zwykle.od, zwykle.do) }, '. Bez budżetu wykonawcy zobaczą te widełki.']
      : ['Bez budżetu wykonawcy wycenią zlecenie według swoich cen. Kwota pomaga im ocenić, czy zlecenie jest dla nich.']
  } else if (zestaw && kwota <= zestaw) {
    wynik.wartosc = 'Sprawdź kwotę'
    wynik.uwaga = true
    wynik.tekst = [...wstep, 'Ta kwota nie pokrywa zestawu. Podaj budżet na całość albo zostaw pole puste.']
  } else {
    const znak = zestaw ? kwota - zestaw : kwota
    const z = zakresDlaWykonawcow(znak, porownuj ? zwykle : null)
    const naMontaz = zestaw ? ['Na montaż zostaje ok. ', { b: zl(znak) }, '. '] : []
    const widac = { b: zakresZl(z.od, z.do) }
    wynik.znak = znak
    wynik.zakres = z
    wynik.wartosc = zakresZl(z.od, z.do)
    if (z.zrodlo === 'powyzej') {
      wynik.uwaga = true
      wynik.tekst = [...wstep, ...naMontaz, `To więcej, niż zwykle kosztuje ${co} (${zakresZl(zwykle.od, zwykle.do)}). Wykonawcy zobaczą zakres `, widac, '.']
    } else if (z.zrodlo === 'ponizej') {
      wynik.uwaga = true
      wynik.tekst = [...wstep, ...naMontaz, `To mniej, niż zwykle kosztuje ${co} (${zakresZl(zwykle.od, zwykle.do)}). Wykonawcy zobaczą zakres `, widac, ', ale część może odmówić z powodu kwoty.']
    } else {
      wynik.tekst = [...wstep, ...naMontaz, 'Wykonawcy zobaczą zakres ', widac, ' i wskażą w nim swoją cenę.', ...montazInfo]
    }
  }

  // Skala paska: zwykłe ceny z zapasem po obu stronach, poszerzona, gdy kwota albo zakres wychodzą poza nią.
  const lo = [(porownuj || !kwota) && ma && zwykle.od, wynik.zakres?.od, wynik.znak].filter((x) => x > 0)
  const hi = [(porownuj || !kwota) && ma && zwykle.do, wynik.zakres?.do, wynik.znak].filter((x) => x > 0)
  if (lo.length) {
    const rozpietosc = (porownuj || !kwota) && ma ? zwykle.do - zwykle.od : Math.max(...hi) * 0.4
    const sLo = Math.max(0, Math.min(...lo) - rozpietosc * 0.9)
    const sHi = Math.max(...hi) + rozpietosc * 1.2
    const proc = (n) => Math.max(0, Math.min(100, ((n - sLo) / (sHi - sLo)) * 100))
    const pas = (a, b) => ({ left: `${proc(a)}%`, width: `${proc(b) - proc(a)}%` })
    wynik.pasy = {
      rynek: porownuj || (ma && !kwota) ? pas(zwykle.od, zwykle.do) : null,
      wykonawcy: wynik.zakres ? pas(wynik.zakres.od, wynik.zakres.do) : null,
      znak: wynik.znak ? `${proc(wynik.znak)}%` : null,
    }
  }
  return wynik
}
